import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './GuestPropertyEmail.css';
import { assistantRequest } from '../services/assistantApi';

export default function GuestPropertyEmail({ listingKey, controlRef, onState, onResult }) {
  const [open, setOpen] = useState(false);
  const [recipient, setRecipient] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const busyRef = useRef(false);
  const generation = useRef(0);
  useEffect(() => { generation.current += 1; setOpen(false); setPreview(null); setRecipient(''); setRecipientName(''); setError(''); }, [listingKey]);
  useEffect(() => () => { generation.current += 1; }, []);
  useEffect(() => {
    onState?.({ email_open: open, email_ready: Boolean(preview), email_recipient: preview?.recipient || recipient, email_recipient_name: recipientName });
  }, [open, preview, recipient, recipientName, onState]);

  const prepare = async (address = recipient, name = recipientName) => {
    if (busyRef.current) return 'The email preview is still being prepared.';
    setOpen(true); setRecipient(address); setRecipientName(name); setPreview(null); setError('');
    if (!name.trim()) {
      const message = 'What is the recipient’s name? Enter their name and email address to preview this property email.';
      if (address.trim()) setError(message);
      return message;
    }
    if (!address.trim()) return 'What email address should receive this property? You can also type it into the form.';
    busyRef.current = true; setBusy(true);
    const version = generation.current;
    try {
      const result = await assistantRequest('/email/prepare', { listing_key: listingKey, recipient: address.trim(), recipient_name: name.trim() });
      if (version !== generation.current) return 'The property changed. Please prepare a new email.';
      setPreview(result);
      return `The property email is ready for ${result.recipient}. Please check the recipient and preview, then say send it or press Send email.`;
    } catch (e) { if (version === generation.current) setError(e.message); return e.message; }
    finally { busyRef.current = false; setBusy(false); }
  };
  const send = async () => {
    if (!preview || busyRef.current) return 'Please prepare and review the recipient and email preview first.';
    const current = preview;
    busyRef.current = true; setBusy(true); setError('');
    // A send ticket is one-use. Never automatically retry an uncertain send.
    setPreview(null);
    try {
      const result = await assistantRequest('/email/send', { ticket: current.ticket });
      setOpen(false);
      return `The email service accepted the property email for ${result.recipient}.`;
    } catch (e) { setError(e.message); return e.message; }
    finally { busyRef.current = false; setBusy(false); }
  };
  useImperativeHandle(controlRef, () => ({ prepare, send, close: () => { generation.current += 1; setOpen(false); setPreview(null); return 'Email canceled.'; } }));
  if (!open) return null;
  return createPortal(<div className="assistant-email-overlay"><div data-assistant-surface="email" className="assistant-email-dialog" role="dialog" aria-modal="true" aria-label="Email this property" onClick={e => e.stopPropagation()}>
    <div className="assistant-email-content">
      <button className="assistant-email-close" onClick={() => { generation.current += 1; setOpen(false); setPreview(null); }} aria-label="Close property email">×</button>
      <h3>Email this property</h3>
      <form className="assistant-share-form" onSubmit={async e => { e.preventDefault(); const result = preview ? await send() : await prepare(); onResult?.(result); }}>
        <label htmlFor="assistant-property-recipient-name">Recipient name</label>
        <input id="assistant-property-recipient-name" type="text" autoComplete="name" autoFocus required maxLength={100} value={recipientName} disabled={busy} onChange={e => { setRecipientName(e.target.value); setPreview(null); setError(''); }} />
        <label htmlFor="assistant-property-recipient">Recipient email</label>
        <input id="assistant-property-recipient" type="email" autoComplete="email" required value={recipient} disabled={busy} onChange={e => { setRecipient(e.target.value); setPreview(null); setError(''); }} />
        {preview && <><p>Send this listing to <strong>{recipientName.trim()} ({preview.recipient})</strong>?</p><iframe sandbox="" referrerPolicy="no-referrer" title="Property email preview" srcDoc={preview.html} /></>}
        {error && <p role="alert">{error}</p>}
        <button type="submit" disabled={busy}>{busy ? 'Please wait…' : preview ? 'Send email' : 'Preview email'}</button>
      </form>
    </div>
  </div></div>, document.body);
}
